import type { RefObject } from "react"
import { useEffect } from "react"

import { useScroll } from "~/components/foundations/ScrollProvider"

/** px over which the hero fades between fully exposed and fully covered. */
const DEFAULT_FADE_DISTANCE = 320

/**
 * Fades a sticky hero module in and out based on the scroll position of the
 * module immediately below it.
 *
 * While the hero is sticky (`position: sticky`), the next sibling module scrolls
 * over it. The hero is faded out as that module's top reaches the top of the
 * viewport (offset `0`) and faded back in as it slides down and re-exposes the
 * hero (offset `> 0`). The fade is a no-op while the hero is not sticky.
 *
 * @param ref - Ref to the hero element to fade.
 * @param fadeDistance - _Optional_ px over which the hero fades. Defaults to
 * `320`.
 */
export const useStickyHeroFade = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  fadeDistance: number = DEFAULT_FADE_DISTANCE,
) => {
  const { lenis } = useScroll()

  useEffect(() => {
    const hero = ref.current
    if (!lenis || !hero) return

    const update = () => {
      const nextEl = hero.nextElementSibling as HTMLElement | null

      // Only fade while the hero is sticky.
      if (!nextEl || getComputedStyle(hero).position !== "sticky") {
        hero.style.removeProperty("opacity")
        return
      }

      const { top } = nextEl.getBoundingClientRect()
      const opacity = Math.min(
        Math.max((top + fadeDistance) / fadeDistance, 0),
        1,
      )
      hero.style.opacity = String(opacity)
    }

    update()
    lenis.on("scroll", update)
    window.addEventListener("resize", update)

    return () => {
      lenis.off("scroll", update)
      window.removeEventListener("resize", update)
      hero.style.removeProperty("opacity")
    }
  }, [lenis, ref, fadeDistance])
}
